import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBoolean } from "../../hooks/useBoolean";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  makeStyles,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import WRAP from "WRAP-JS";
import {
  _debounce,
  formatLonLatDM,
  formatTimeZoneDate,
  m2ft,
  mps2kt,
  normalizeDegree,
  to360degreesRange
} from "../../utils/FPEUtils";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import LaunchIcon from '@material-ui/icons/Launch';
import PlaceIcon from '@material-ui/icons/Place';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useAlert } from "../../hooks/useAlert";
import { configElevationDh } from "./layers/config/ConfigElevation";
import { dhConfigCalendarSun } from "./layers/config/ConfigCalendar";
import {
  COMMUNICATE_ERROR_MESSAGE_ELEVATION_DATA,
  COMMUNICATE_ERROR_MESSAGE_SUNRISE_DATA,
  COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER,
  COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
} from "../../constants/ErrorMessage";
import { ReactComponent as ImageSun } from '../../images/contents/map/mappoint/sun.svg';
import { ReactComponent as ImageArrowUp } from '../../images/contents/map/mappoint/arrow-up.svg';
import { ReactComponent as ImageArrowDown } from '../../images/contents/map/mappoint/arrow-down.svg';
import { useErrorMessage } from "../../store/AlertState";
import { useRecoilValue } from "recoil";
import { timeZoneSelector } from "../../store/TimeZoneState";
import { useTranslation } from "react-i18next";
import { WX_ID_GFS, WX_ID_LFM, WX_ID_MSM } from '../../store/WeatherContentsState';
import {
  CONTENTS_ID_POINT_SUMMARY,
  disabledContentsWithID,
  useDisabledContents
} from '../../store/DisabledContentsState';
import PlayCircleFilledWhiteIcon from '@material-ui/icons/PlayCircleFilledWhite';
import axios from "axios";
import { Loading } from "../../parts/Loading";
import { mapRegionState } from "../../store/MapRegionState";
import { usePrevious } from "../../hooks/usePrevious";

const useStyles = makeStyles({
  dialogRoot: {
    '& .MuiPaper-root': {
      backgroundColor: 'rgba(30,30,30,.7)',

    },
    '& .MuiDialogContentText-root': {
      color: '#eee'
    }
  },
  sunSelect: {
    color: '#eee'
  },
  sunContainer: {
    display: 'flex',
    justifyContent: 'start',
    columnGap: '0.5rem'
  },
  sunBox: {
    display: 'flex',
    alignItems: 'center'
  },
  sunIcon: {
    width: 20,
    height: 20,
    marginLeft: -5,
  },
  sunArrowIcon: {
    width: 14,
    height: 14,
  },
  btn: {
    width: '100%',
    textTransform: 'none',
    '&:disabled': {
      color: '#999'
    }
  },
  launchIcon: {
    position: 'absolute',
    right: 3,
    fill: '#696969'
  },
  tabElem: {
    padding: 0,
    minWidth: 40,
    lineHeight: 0.9,
    textTransform: 'none',
  },
  gpvRow: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: 2
  },
  gpvLabel: {
    width: 50
  },
  gpvContents: {
    marginLeft: 5
  },
  gpvButton: {
    '&.MuiButton-outlined': {
      color: 'white',
      borderColor: 'white'
    },
    '&:disabled': {
      color: '#999',
      borderColor: '#999'
    }
  },
  addressButton: {
    '&.MuiButton-outlined': {
      color: 'white',
      borderColor: 'white'
    },
  },
  customIndicator: {
    backgroundColor: 'white',
  },
  pointInfoDescription: {
    fontSize: '1rem',
    // fontFamily: "Roboto", "Helvetica", "Arial", sans-serif,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
    marginBottom: 12,
    color: '#eee',
  },
  pointInfoLonLatContainer: {
    maxWidth: 'fit-content'
  },
  pointInfoLonLat: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  selectValid: {
    color: '#eee',
    marginLeft: 10
  },
  loadingBackdrop: {
    height: "100%",
    width: "100%",
    backgroundColor: "rgba(0,0,0,0.3)",
    position: "absolute"
  },
  externalLinkButtonBox: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
  },
});

const AltitudeDataTabPanel = (props) => {
  const {lonLat, detail, altitude, validTime, validTimes, updateSelectedValidtime, gpvData, isLoading} = props;
  const classes = useStyles();
  const [lat, lon] = lonLat.split(',');
  const timeZone = useRecoilValue(timeZoneSelector);
  const {disabled} = useDisabledContents();

  const handleBasicWeatherClick = useCallback(() => {
    const url = `${process.env.REACT_APP_WX_FORECAST_LINK_URL}${lat}/${lon}`;
    window.open(url, '_blank');
  }, [lat, lon]);

  const handlePointSummaryClick = useCallback(() => {
    const url = `${process.env.REACT_APP_PRO_POINT_SUMMARY_URL}?lon=${lon}&lat=${lat}`;
    window.open(url, '_blank');
  }, [lat, lon]);

  const handleEmagramClick = useCallback(() => {
    const url = `${process.env.REACT_APP_PRO_POINT_SUMMARY_URL}emagram?lon=${lon}&lat=${lat}`;
    window.open(url, '_blank');
  }, [lat, lon]);

  const changeValid = (num) => {
    const idx = validTime.index + num;
    const target = validTimes[idx];
    if (target !== undefined) {
      updateSelectedValidtime({index: idx, value: target});
    }
  }

  const handleValidChange = (event) => {
    const value = event.target.value;
    updateSelectedValidtime({index: validTimes.indexOf(value), value: value});
  }

  return (
    <Box
      role="tabpanel"
      hidden={detail.value !== altitude}
      id={`simple-tabpanel-${detail.value}`}
      aria-labelledby={`simple-tab-${detail.value}`}
    >
      <Box>
        <Box className={classes.gpvRow}>
          <Box className={classes.gpvRow}>
            <Box>Valid Time: </Box>
            {validTimes.length > 0 && validTime.value && <Select
              className={classes.selectValid}
              value={validTime.value}
              onChange={handleValidChange}
            >
              {validTimes.map((vt) => {
                return (
                  <MenuItem
                    key={vt}
                    value={vt}
                  >{formatTimeZoneDate(new Date(vt), timeZone, 'YYYY/MM/DD HH:mmz')}</MenuItem>
                );
              })}
            </Select>}
          </Box>
          <Box className={classes.gpvContents}>
            <IconButton
              size="small"
              onClick={() => changeValid(-1)}
              disabled={validTime.index === 0}
            >
              <PlayCircleFilledWhiteIcon style={{
                color: validTime.index === 0 ? "gray" : "white",
                transform: "rotate(180deg)"
              }}/>
            </IconButton>
            <IconButton
              size="small"
              onClick={() => changeValid(1)}
              disabled={validTime.index === validTimes.length - 1}
            >
              <PlayCircleFilledWhiteIcon style={{
                color: validTime.index === validTimes.length - 1 ? "gray" : "white",
              }}/>
            </IconButton>
          </Box>
        </Box>
        <Box style={{display: "flex", justifyContent: "space-between"}}>
          <Box style={{flex: 1, position: "relative"}}>
            {
              isLoading && <Box className={classes.loadingBackdrop}>
                <Loading />
              </Box>
            }
            <Box className={classes.gpvRow}>
              <Box className={classes.gpvLabel}>Wind:</Box>
              <Box> {formatNumber(gpvData[altitude]?.windDir)}° / {formatNumber(gpvData[altitude]?.windKnt)}kt</Box>
            </Box>
            {gpvData[altitude]?.vis !== undefined && <Box className={classes.gpvRow}>
              <Box className={classes.gpvLabel}>VIS:</Box>
              <Box>{formatNumber(gpvData[altitude]?.vis)}m</Box>
            </Box>}
            <Box className={classes.gpvRow}>
              <Box className={classes.gpvLabel}>Temp:</Box>
              <Box>{formatNumber(gpvData[altitude]?.temp)}℃</Box>
            </Box>
            <Box className={classes.gpvRow}>
              <Box className={classes.gpvLabel}>RH:</Box>
              <Box>{formatNumber(gpvData[altitude]?.rh)}%</Box>
            </Box>
          </Box>
          <Box className={classes.externalLinkButtonBox} style={{flex: 2}}>
            <Box>
              <Button variant="contained" className={classes.btn} onClick={handleBasicWeatherClick}>
                Basic Weather forecast
                <LaunchIcon className={classes.launchIcon}/>
              </Button>
            </Box>
            <Box>
              <Button disabled={disabled[CONTENTS_ID_POINT_SUMMARY]} variant="contained" className={classes.btn} onClick={handlePointSummaryClick}>
              FOSTER-Links Pro - Point Summary
                <LaunchIcon className={classes.launchIcon}/>
              </Button>
            </Box>
            <Box>
              <Button disabled={disabled[CONTENTS_ID_POINT_SUMMARY]} variant="contained" className={classes.btn} onClick={handleEmagramClick}>
              FOSTER-Links Pro - Emagram
                <LaunchIcon className={classes.launchIcon}/>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const EXPO_CALENDAR_OPTION = {
  TZ: 'Asia/Tokyo',
  OFFSET: 9
};
const POINT_ICON_SIZE = 32;
const MapPointInfoDialog = () => {
  const [open, showDialog, closeDialog] = useBoolean(false);
  const [screenPoint, setScreenPoint] = useState({});
  const [lonLatDM, setLonLatDM] = useState('');
  const [lonLat, setLonLat] = useState('');
  const [pointData, setPointData] = useState({});
  const [pointDataParam, setPointDataParam] = useState(null);
  const [altitude, setAltitude] = useState('0');
  const [addressVisible, setAddressVisible] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [address, setAddress] = useState(null);
  const [selectedValidTime, setSelectedValidtime] = useState({});
  const [validTimes, setValidTimes] = useState([]);
  const [gpvData, setGpvData] = useState({});
  const {showSuccess} = useAlert();
  const timeZone = useRecoilValue(timeZoneSelector);
  const {t} = useTranslation();

  const lfmDisable = useRecoilValue(disabledContentsWithID(WX_ID_LFM));
  const msmDisable = useRecoilValue(disabledContentsWithID(WX_ID_MSM));
  const gfsDisable = useRecoilValue(disabledContentsWithID(WX_ID_GFS));
  const [model, setModel] = useState(!lfmDisable ? 'lfm' : !msmDisable ? 'msm' : !gfsDisable ? 'gfs' : null);
  const [isLoading, setIsLoading] = useState(false);

  const classes = useStyles();

  const {getPointData} = usePointData();
  const { getAddress, language } = useAddressData();

  // 比較用に (lat, lon, language) をまとめたオブジェクトを追跡
  const currentAddressKey = pointDataParam
    ? { address, lat: pointDataParam.lat, lon: pointDataParam.lon, language }
    : null;
  const prevAddressKey = usePrevious(currentAddressKey);

  const prevLonLat = useRef();
  const handleMapClick = useCallback(async (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const screenPt = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    }
    setScreenPoint(screenPt);
    const pt = WRAP.Geo.getPoint(new WRAP.Geo.ScreenPoint(screenPt.x, screenPt.y));
    //DM表記は小数点2桁まで
    const lonLatDM = formatLonLatDM(pt.lonDegree(), pt.latDegree(), 2);
    setLonLatDM(lonLatDM);
    //10進表記は小数点6桁（7桁目を四捨五入）
    const depth = Math.pow(10, 6);
    const lat = Math.round(pt.latDegree() * depth) / depth;
    const lon = Math.round(pt.lonDegree() * depth) / depth;
    setLonLat((currentLonLat) => {
      prevLonLat.current = currentLonLat;
      return `${lat},${lon}`;
    });

    setPointDataParam({
      lat: pt.latDegree(),
      lon: pt.lonDegree(),
      validTime: formatTimeZoneDate(new Date(), EXPO_CALENDAR_OPTION.TZ, 'YYYYMMDD')
    });
    showDialog();
  }, [showDialog])

  const closePointInfoDialog = ()=> {
    setAddressVisible(false);
    setAddress(null);
    closeDialog();
  }

  useEffect(() => {
    // mapDivにイベント追加
    const mapDiv = document.getElementById('map-container');

    mapDiv.addEventListener('mousedown', (e) => {
      // 右クリック以外はダイアログ閉じる
      if (e.button !== 2) {
        closePointInfoDialog();
      }
    }, true);
    WRAP.Geo.addEventHandler('boundsChange', _debounce(() => {
      //画角変更を拾ってダイアログ閉じる
      closePointInfoDialog();
      // 初回のイベントを実行、以降500msの間は無視
    }, 500, {leading: true, trailing: false}));

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleRightClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      void handleMapClick(event);
    };
    const mapDiv = document.getElementById('map-container');
    mapDiv.addEventListener('contextmenu', handleRightClick);

    return () => {
      mapDiv.removeEventListener('contextmenu', handleRightClick);
    };
  }, [handleMapClick]);

  useEffect(() => {
    const getPoint = async () => {
      if (pointDataParam) {
        const p = await getPointData({lat: pointDataParam.lat, lon: pointDataParam.lon, validTime: pointDataParam.validTime});
        setPointData(p);
      }
    };
    getPoint();
  }, [pointDataParam, getPointData]);

  const handleCopyLonLat = useCallback(() => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(lonLat).then(() => {
        showSuccess('copied to clipboard.', {timeout: 2000, close: false});
      });
    }
  }, [lonLat, showSuccess]);

  const handleCopyLonLatDM = useCallback(() => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(lonLatDM).then(() => {
        showSuccess('copied to clipboard.', {timeout: 2000, close: false});
      });
    }
  }, [lonLatDM, showSuccess]);

  const handleToggleAddress = ()=> {
    setAddressVisible(!addressVisible);
  }

  useEffect(() => {
    const getData = async () => {
      if (pointDataParam) {
        const prevLat = prevAddressKey?.lat;
        const prevLon = prevAddressKey?.lon;
        const prevLang = prevAddressKey?.language;

        // 前回と同じ座標ならスキップ。ただし言語切替の場合は実行
        if (
          prevAddressKey &&
          prevAddressKey.address !== null &&
          prevLat === pointDataParam.lat &&
          prevLon === pointDataParam.lon &&
          prevLang === language
        ) {
          return;
        } else if (addressLoading) {
          return;
        }
        setAddressLoading(true);
        try {
          const address = await getAddress({lat: pointDataParam.lat, lon: pointDataParam.lon});
          setAddress(address);
        } finally {
          setAddressLoading(false);
        }
      }
    };
    if (addressVisible) {
      void getData(pointDataParam.lat, pointDataParam.lon, language);
    }
  }, [addressVisible, pointDataParam, prevAddressKey, getAddress, language, addressLoading]);

  const formatTime = (value, aqc) => {
    if (aqc === 1) {
      return t('white night');
    } else if (aqc === 2) {
      return t('polar night');
    }
    return value !== undefined ? formatTimeZoneDate(new Date(value), timeZone, 'MM/DD HH:mmz') : '---';
  };

  const prevAlt = useRef();
  const changeAltitude = (_, altitude) => {
    setAltitude((currentAlt) => {
      prevAlt.current = currentAlt;
      return altitude
    });
  }

  const prevModel = useRef();
  const selectModel = (target) => {
    setModel((currentModel) => {
      prevModel.current = currentModel;
      return target;
    });
  }

  const baseTime = useRef();
  useEffect(() => {
    const asyncFunc = async () => {
      // 異なるModelが選択された場合、高度がGround,Upperで変わった場合、Timelistを取り直す
      if (model && lonLat
        && (
          (prevModel.current !== model)
          || (prevLonLat.current !== lonLat)
          || (model !== 'gfs' && prevAlt.current === '0' && altitude !== '0')
          || (model !== 'gfs' && prevAlt.current !== '0' && altitude === '0')
        )) {
        const timelist = await getGpvTimelist(model, altitude, selectedValidTime);
        baseTime.current = timelist.basetime;
        setSelectedValidtime(timelist.selectedValidTime);
        setValidTimes(timelist.validTimes);
        // Modelまたは高度が変わった際にvalidtimeが変わらない場合、selectedValidTime.valueが発火しないので自前でデータを取りに行く
        if (selectedValidTime.value === timelist.selectedValidTime.value) {
          setIsLoading(true);
          const newGpvData = await getGpvData(model, altitude, timelist.selectedValidTime.value, lonLat, timelist.basetime);
          setIsLoading(false);
          setGpvData(newGpvData);
        }
        prevModel.current = model;
        prevAlt.current = altitude;
        prevLonLat.current = lonLat;
      }
    };
    asyncFunc();
    // eslint-disable-next-line
  }, [model, altitude, lonLat]);

  useEffect(() => {
    const asyncFunc = async () => {
      setIsLoading(true);
      const newGpvData = await getGpvData(model, altitude, selectedValidTime.value, lonLat, baseTime.current);
      setIsLoading(false);
      setGpvData(newGpvData);
    }
    asyncFunc();
    // eslint-disable-next-line
  }, [selectedValidTime.value]);

  const updateSelectedValidtime = useCallback((value) => {
    setSelectedValidtime(value);
  }, []);

  const handleChangeSunTargetDate = useCallback((event) => {
    setPointDataParam({
      ...pointDataParam,
      validTime: event.target.value,
    })
  }, [pointDataParam]);

  return (
    <>
      {open &&
        <PlaceIcon style={{
          position: 'absolute',
          top: screenPoint.y - POINT_ICON_SIZE,
          left: screenPoint.x - (POINT_ICON_SIZE / 2),
          width: POINT_ICON_SIZE,
          height: POINT_ICON_SIZE,
          zIndex: 2,
          fill: 'rgb(251,62,66)'
        }} viewBox="0 0 22 22"/>
      }
      <Dialog
        open={open}
        onClose={closePointInfoDialog}
        hideBackdrop={true}
        aria-labelledby="point-info-dialog-title"
        aria-describedby="point-info-description"
        className={classes.dialogRoot}
        style={{pointerEvents: 'none'}}
        PaperProps={{style: {pointerEvents: 'auto'}}}
      >
        <IconButton size="small" style={{
          position: 'absolute',
          top: 0,
          right: 0,
          color: 'rgb(30,30,30)'
        }} onClick={closePointInfoDialog}>
          <CloseIcon/>
        </IconButton>
        <DialogContent>
          <Box className={classes.pointInfoDescription} id="point-info-description">
            <ul>
              <li>
                <ul className={classes.pointInfoLonLatContainer}>
                  <li className={classes.pointInfoLonLat}>
                    {lonLatDM}
                    {(window.location.protocol === 'https:' && navigator.clipboard) &&
                      <IconButton aria-label="copy" size="small" onClick={handleCopyLonLatDM}>
                        <Tooltip title={`copy position`} arrow>
                          <FileCopyIcon fontSize="small" style={{color: 'eee'}}/>
                        </Tooltip>
                      </IconButton>}
                  </li>
                  <li className={classes.pointInfoLonLat}>
                    {lonLat}
                    {(window.location.protocol === 'https:' && navigator.clipboard) &&
                      <IconButton aria-label="copy" size="small" onClick={handleCopyLonLat}>
                        <Tooltip title={`copy position`} arrow>
                          <FileCopyIcon fontSize="small" style={{color: 'eee'}}/>
                        </Tooltip>
                      </IconButton>}
                  </li>
                </ul>
              </li>
              <li>ELV: {pointData.elevation ?? '---'} ft</li>
              <li>
                <Button variant={"outlined"} size={"small"} className={classes.addressButton} endIcon={addressVisible?<ExpandLessIcon/>:<ExpandMoreIcon/>} onClick={handleToggleAddress}>{t('Show Address')}</Button>
                { addressVisible &&
                <Box style={{flex: 1, position: "relative"}}>
                  {
                    addressLoading && <Box className={classes.loadingBackdrop}>
                      <Loading />
                    </Box>
                  }
                  {address}
                </Box>
                }
              </li>
              <li style={{marginTop: 5}}>
                <div className={classes.sunContainer}>
                  <div className={classes.sunBox}>
                    <Select
                      className={classes.sunSelect}
                      value={pointDataParam?.validTime}
                      onChange={handleChangeSunTargetDate}
                    >
                      {Array.from({ length: 15 }, (_, i) => {
                        const d = new Date();
                        d.setDate(d.getDate() + i - 7);
                        const value = formatTimeZoneDate(d, EXPO_CALENDAR_OPTION.TZ, 'YYYYMMDD');
                        const label = formatTimeZoneDate(d, EXPO_CALENDAR_OPTION.TZ, 'MM/DD');
                        return <MenuItem key={i} value={value}>{label}</MenuItem>;
                      })}
                    </Select>
                  </div>
                  <div className={classes.sunBox}>
                    <ImageArrowUp className={classes.sunArrowIcon}/><ImageSun className={classes.sunIcon}/>
                    {formatTime(
                      timeZone === "utc"
                        ? pointData.sunrise
                        : pointData.sunriseLt,
                      pointData.aqc
                    )}
                  </div>
                  <div className={classes.sunBox}>
                    <ImageArrowDown className={classes.sunArrowIcon} /><ImageSun className={classes.sunIcon} />
                    {formatTime(
                      pointData.aqc === 0
                        ? timeZone === "utc"
                          ? pointData.sunset
                          : pointData.sunsetLt
                        : undefined
                    )}
                  </div>
                </div>
              </li>
            </ul>
          </Box>
          <Box sx={{ width: '100%', color: 'white'}}>
            <Box>
              <Button onClick={() => selectModel("lfm")} variant={model === "lfm" ? "contained" : "outlined"} disabled={lfmDisable} className={classes.gpvButton}>LFM</Button>
              <Button onClick={() => selectModel("msm")} variant={model === "msm" ? "contained" : "outlined"} disabled={msmDisable} className={classes.gpvButton}>MSM</Button>
              <Button onClick={() => selectModel("gfs")} variant={model === "gfs" ? "contained" : "outlined"} disabled={gfsDisable} className={classes.gpvButton}>gfs</Button>
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={altitude}
                onChange={changeAltitude}
                aria-label="basic tabs example"
                variant="fullWidth"
                classes={{indicator: classes.customIndicator}}
              >
                {altitudes.map((l, index) => <Tab key={index} className={classes.tabElem} {...l}></Tab>)}
              </Tabs>
            </Box>
            {altitudes.map((l, i) => {
              return (
                <AltitudeDataTabPanel
                  key={`AltitudeDataTabPanel-${i}`}
                  lonLat={lonLat}
                  detail={l}
                  altitude={altitude}
                  validTime={selectedValidTime}
                  validTimes={validTimes}
                  updateSelectedValidtime={updateSelectedValidtime}
                  gpvData={gpvData}
                  isLoading={isLoading}
                />
              )
            })}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};


export default MapPointInfoDialog;

const useAddressData = () => {
  const {i18n} = useTranslation();
  const region = useRecoilValue(mapRegionState);
  const {showWarning, showError} = useAlert();
  const getAddress = useCallback(async ({lon, lat}) => {
      const apiKey= process.env.REACT_APP_GOOGLEMAP_API_KEY;
      return axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${apiKey}&language=${i18n.language}&region=${region}`,
      ).then(response => {
        if (response.data.status === 'OK') {
          if (response.data.results !== undefined && response.data.results.length > 0) {
            return response.data.results[0].formatted_address;
          } else {
            return '---';
          }
        } else {
          if (response.data.status === 'ZERO_RESULTS') {
            showWarning('Address did not found.', {timeout: 3000})
          } else {
            showError('Failed to get address.', {timeout: 3000})
          }
          return '---'
        }
      })
    },
    [i18n.language, region, showWarning, showError]
  );
  return { getAddress, language: i18n.language };
}
const usePointData = ()=>{
  const {dhElevation, dhCalendarSun} = useWrapDh();
  const {showErrorWithMessage} = useErrorMessage();

  const getPointData = useCallback(async ({lon, lat, validTime}) => {
    const promises = [];
    //Elevation(標高)のpointValue
    const elevP = new Promise((resolve, reject) => {
      dhElevation.getPointValue('data', [lon, lat], null, null, (value) => {
        resolve({elevation: value});
      }, () => {
        showErrorWithMessage({
          message: COMMUNICATE_ERROR_MESSAGE_ELEVATION_DATA,
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        });
        reject({elevation: undefined});
      });
    });
    promises.push(elevP);

    //日の出日の入り
    promises.push(new Promise((resolve, reject) => {
      elevP.then((value) => {
        if (value.elevation !== undefined) {
          // 基準日はJSTの日付、time_diffは 9 でリクエスト
          dhCalendarSun.load('data', {
            valid_time: validTime ?? formatTimeZoneDate(new Date(), EXPO_CALENDAR_OPTION.TZ, 'YYYYMMDD'),
            points: JSON.stringify([{
              NAME: "",
              LOND: lon,
              LATD: lat,
              ALT: value.elevation
            }]),
            time_diff: JSON.stringify([{
              NAME: "",
              DIFF: EXPO_CALENDAR_OPTION.OFFSET,
            }]),
          }, false, (data) => {
            resolve(data);
          }, () => {
            showErrorWithMessage({
              message: COMMUNICATE_ERROR_MESSAGE_SUNRISE_DATA,
              additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER,
            });
            reject();
          });
        } else {
          reject();
        }
      })
      .catch(() => {
        reject();
      });
    }));

    return await Promise.allSettled(promises).then(values => {
      let point = {}
      values.forEach(v => {
        if (v.status === 'fulfilled') {
          if (v.value.elevation !== undefined) {
            // elevation
            point = {...point, ...{elevation: Math.round(m2ft(v.value.elevation)).toLocaleString()}}
          } else {
            // sunrise, sunset
            for (const p of v.value.data) {
              point = {...point, ...{
                aqc:p.AQC,
                sunrise:p.rise_time,
                sunset: p.set_time,
                sunriseLt:p.rise_time_LT,
                sunsetLt: p.set_time_LT
              }};
            }
          }
        }
      });
      return point;
    });
    // eslint-disable-next-line
  }, []);

  return {getPointData};
}

const useWrapDh = () => {
  const {dhElevation, dhCalendarSun} = useMemo(() => {
    const elevation = new WRAP.Data(configElevationDh);
    const calendarSun = new WRAP.Data(dhConfigCalendarSun);
    return {
      dhElevation: elevation,
      dhCalendarSun: calendarSun
    }
  }, []);

  return {
    dhElevation,
    dhCalendarSun
  };
};

const altitudes = [
  {label: 'SFC', value: '0'},
  {label: '900hPa (3000ft)', value: '900'},
  {label: '850hPa (FL050)', value: '850'},
  {label: '800hPa (FL070)', value: '800'},
  {label: '700hPa (FL100)', value: '700'},
  {label: '500hPa (FL180)', value: '500'}
];

const getTargetAltitudeValues = (model, altitude) => {
  let filteredAltList = []
  if (model === 'gfs') {
    filteredAltList = altitudes;
  } else if (altitude !== '0') {
    filteredAltList = altitudes.filter(alt => alt.value !== '0')
  } else {
    filteredAltList = altitudes.filter(alt => alt.value === '0');
  }

  return filteredAltList.map(alt => alt.value);
}

const getGpvTimelist = (model, altitude, selectedValidTime) => {
  if (!selectedValidTime || !model) return {};
  const dataName = getDataName(model, altitude);
  return axios.get(
    `${process.env.REACT_APP_EXPO_NUMERICAL_DB_GPV_URL}/${dataName}/valid_times`
  ).then(response => {
    const ret = {};
    ret.basetime = response.data.basetime;
    ret.validTimes = response.data.valid_times;

    // 選択しているvalidTimeが無い場合は、現在時刻に一番近い正時
    const index = ret.validTimes.indexOf(selectedValidTime.value);
    if (index < 0) {
      let base;
      if(selectedValidTime.value !== undefined) {
        // 選択中のvalidtimeが有る場合はvalidTimeリストの中の最も近いvalidTime
        base = new Date(selectedValidTime.value)
      } else {
        // 選択中のvalidtimeが無い場合は現在時刻に最も近いvalidTime
        base = new Date();
      }
      let minDiff;
      let minValidIndex;
      ret.validTimes.forEach((valid, i) => {
        const time = new Date(valid);
        const diff = Math.abs(time - base);
        if (!minDiff || diff < minDiff) {
          minDiff = diff;
          minValidIndex = i;
        }
      });
      ret.selectedValidTime = {index: minValidIndex, value: ret.validTimes[minValidIndex]};
    } else {
      ret.selectedValidTime = {index: index, value: ret.validTimes[index]};
    }
    return ret;
  });
}

const getGpvData = (model, altitude, validTime, lonLat, basetime) => {
  if (!validTime || !lonLat) return {};
  const dataName = getDataName(model, altitude);
  const elements = getElements(model);
  const level = getTargetAltitudeValues(model, altitude);
  let [lat, lon] = lonLat.split(',');
  lon = to360degreesRange(Number(lon));

  return axios.post(
    `${process.env.REACT_APP_EXPO_NUMERICAL_DB_GPV_URL}/${dataName}/point_data`,
    JSON.stringify({
      valid_time: [validTime],
      level: level,
      elements: elements,
      points: [
        {
          NAME: `${lat}-${lon}`,
          LOND: Number(lon),
          LATD: Number(lat)
        }
      ],
      basetime: basetime
    })
  ).then(response => {
    const ret = {};

    response.data.time.forEach((t, i) => {
      const levelData = {};

      const vWind = response.data.VWIND[i][0];
      const uWind = response.data.UWIND[i][0];
      if (vWind === -9999 && uWind === -9999) {
        levelData.windDir = -9999;
        levelData.windKnt = -9999;
      } else {
        const { direction: wDir, speed: wSpd } = calcWind(response.data.VWIND[i][0], response.data.UWIND[i][0]);
        levelData.windDir = normalizeDegree(Math.round(wDir));
        levelData.windKnt = mps2kt(wSpd);
      }

      if (response.data.AIRTMP[i][0] === -9999) {
        levelData.temp = -9999
      } else {
        levelData.temp = kelvinToDegreeCelsius(response.data.AIRTMP[i][0])
      }

      levelData.vis = response.data.VIS?.[i][0];
      levelData.rh = response.data.RHUM[i][0]

      ret[t.level] = levelData;
    })

    return ret;
  });
}

const getDataName = (model, altitude) => {
  let target;
  if (model === 'lfm') {
    if (altitude === '0') {
      target = 'JMA_SRF_LFM';
    } else {
      target = 'JMA_SRF_LFM_UPPER';
    }
  } else if (model === 'msm') {
    if (altitude === '0') {
      target = 'JMA_SRF_MSM';
    } else {
      target = 'JMA_SRF_MSM_UPPER';
    }
  } else if (model === 'gfs') {
    target = 'NCEP_MRF_GFS_0p25';
  }
  return target;
}

const getElements = (model) => {
  const elements = ['AIRTMP', 'RHUM', 'UWIND', 'VWIND'];
  if (model === 'gfs') {
    return elements.concat('VIS');
  } else {
    return elements;
  }
};

const calcWind = (vWind, uWind) => {
  const speed = Math.hypot(vWind, uWind);
  const direction = Math.atan2(uWind, vWind) * (180 / Math.PI) + 180;
  return {
    direction,
    speed,
  };
};

const kelvinToDegreeCelsius = (temperature) => {
  return temperature - 273.15;
};

const formatNumber = (num) => {
  if (num === undefined || num === -9999) {
    return '---';
  }
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
};
